import { Component, inject, input, OnInit, output, signal } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

import { Constants } from "../../../constants/constants";
import { PageMode } from "../../../constants/enums";
import { AgmTask, TableActionEvent, ValidatorModelType, ValueText } from "../../../models";
import { LanguageService, ValidatorApiService } from "../../../services";
import { ConfirmationModalComponent } from "../../general";
import { DataTableComponent } from "../../table/data-table/data-table";
import { AgmTaskModalComponent } from "../agm-task-modal/agm-task-modal";

@Component({
  selector: "adaa-agm-tasks-list",
  imports: [DataTableComponent],
  templateUrl: "./agm-tasks-list.html",
  styleUrl: "./agm-tasks-list.scss",
})
export class AgmTasksListComponent implements OnInit {
  private readonly _validatorApiService = inject(ValidatorApiService);
  private readonly _modalService = inject(NgbModal);
  private readonly _languageService = inject(LanguageService);

  tasks = input.required<AgmTask[]>();
  entities = input.required<ValueText[]>();
  otherEntities = input.required<ValueText[]>();
  parentEntityId = input.required<number | undefined>();
  parentStartDate = input.required<number | undefined>();
  parentEndDate = input.required<number | undefined>();
  tasksChanged = output<AgmTask[]>();

  Constants = Constants;
  formValidation = signal<ValidatorModelType>({
    id: 0,
    status: 0,
    searchKey: "",
    type: 0,
    parameterCatalogs: [],
    nameAE: "",
    nameEN: "",
  });

  public get tasksData(): AgmTask[] {
    return this.tasks()
      .filter((e) => e.status !== Constants.OBJECT_STATUS.REMOVE)
      .map((e, index) => ({
        number: `#${index + 1}`,
        ...e,
      }));
  }

  public ngOnInit(): void {
    this._validatorApiService.searchByKey(Constants.VALIDATORS_CONF_KEY.VALIDATION_SUB_PROJECT_MILESTONE).subscribe({
      next: (response) => {
        if (response.inError) return;

        this.formValidation.set(response.responseData);
      },
    });
  }

  public addNewClicked(): void {
    const task: AgmTask = {
      id: 0,
      startDate: this.parentStartDate() ?? 0,
      endDate: this.parentEndDate() ?? 0,
      completed: false,
      contributingEntities: [],
      nameAE: "",
      nameEN: "",
      fakeId: Math.floor(Math.random() * 217 * 1000),
    };

    this._openTaskModal(task, PageMode.create);
  }

  public actionClicked(action: TableActionEvent): void {
    switch (action.event) {
      case "delete":
        this._deleteTask(action.data);
        break;
      case "edit":
        this._openTaskModal(action.data, PageMode.edit);
        break;
      default:
        break;
    }
  }

  private _openTaskModal(task: AgmTask, pageMode: PageMode): void {
    const modal = this._modalService.open(AgmTaskModalComponent, {
      centered: true,
      size: "xl",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.task = task;
    modal.componentInstance.pageMode = pageMode;
    modal.componentInstance.formValidation = this.formValidation();
    modal.componentInstance.entities = this.entities();
    modal.componentInstance.otherEntities = this.otherEntities();
    modal.componentInstance.parentEntityId = this.parentEntityId();
    modal.componentInstance.parentStartDate = this.parentStartDate();
    modal.componentInstance.parentEndDate = this.parentEndDate();

    modal.result.then((e) => {
      if (!e) return;
      if (pageMode === PageMode.create) this.tasks().push(e);
      else {
        const index = this.tasks().findIndex((task) => (e.fakeId ? e.fakeId === task.fakeId : e.id === task.id));
        if (index > -1) this.tasks().splice(index, 1, e);
      }

      this.tasksChanged.emit(this.tasks());
    });
  }

  private _deleteTask(data: AgmTask): void {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = "national_projects.national_projects_milestone.sub_milestone";
    modal.componentInstance.title = "national_projects.national_projects_milestone.sub_milestone_remove";

    modal.result.then((e) => {
      if (e) this._deleteTaskConfirmed(data);
    });
  }

  private _deleteTaskConfirmed(data: AgmTask): void {
    if (data.fakeId) {
      const taskIndex = this.tasks().findIndex((e) => e.fakeId === data.fakeId);
      if (taskIndex > -1) this.tasks().splice(taskIndex, 1);
    } else {
      const task = this.tasks().find((e) => e.id === data.id);
      if (task) task.status = Constants.OBJECT_STATUS.REMOVE;
    }
    this.tasksChanged.emit(this.tasks());
  }
}
